import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Plug,
  Plus,
  RefreshCw,
  RotateCw,
  ShieldCheck,
  Star,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { adminAudit } from '@/api/admin';
import type { CtidStatus, CtidTradingAccount } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { CtidEventLog } from '@/features/ctids/CtidEventLog';

const TOKEN_WARN_DAYS = 3;

function tokenDaysLeft(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.floor((new Date(iso).getTime() - Date.now()) / 86400_000);
}

function tokenTone(days: number | null): 'up' | 'warn' | 'down' | 'neutral' {
  if (days == null) return 'neutral';
  if (days < 0) return 'down';
  if (days < TOKEN_WARN_DAYS) return 'warn';
  return 'up';
}

const CONN_TONE = (state?: string): 'up' | 'warn' | 'down' | 'neutral' => {
  if (!state) return 'neutral';
  if (state === 'Operational' || state === 'AppAuthenticated') return 'up';
  if (state === 'Reconnecting' || state === 'TlsHandshaking' || state === 'AppAuthenticating' || state === 'AccountAuthenticating') return 'warn';
  if (state === 'Disconnected') return 'down';
  return 'neutral';
};

const statusTone = (status?: string): 'up' | 'warn' | 'neutral' => {
  if (status === 'active') return 'up';
  if (status === 'pending') return 'warn';
  return 'neutral';
};

export function CtidsPage() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['admin', 'ctids'], queryFn: adminAudit.ctids, refetchInterval: 30_000 });
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [oauthState, setOauthState] = useState('');
  const [oauthCode, setOauthCode] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [selectTarget, setSelectTarget] = useState<{ accountId: string; login: string } | null>(null);
  // 提前(在用户点击的同步事件里)打开的空白窗口引用:mutation resolve 后再导航过去,
  // 避免异步回调里再 window.open 被浏览器弹窗拦截器拦掉。不设 noopener/noreferrer 以保留引用,
  // 改用 popup.opener = null 做轻量反向 tabnabbing 防护。
  const oauthPopupRef = useRef<Window | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'ctids'] });

  const startOAuth = useMutation({
    mutationFn: () => adminAudit.startCtidOAuth({ email: email.trim(), displayName: displayName.trim() || undefined }),
    onSuccess: (data) => {
      setOauthState(data.state);
      setAuthUrl(data.authUrl);
      toast.success('授权链接已生成', { description: `有效期至 ${fmtDateTime(data.expiresAt)}` });
      const popup = oauthPopupRef.current;
      if (popup && !popup.closed) {
        popup.location.href = data.authUrl;
      } else {
        // 弹窗被拦截:退回旧行为再试一次(大概率仍被拦,靠下面的"复制链接"按钮兜底)。
        window.open(data.authUrl, '_blank', 'noopener,noreferrer');
      }
      invalidate();
    },
    onError: (err) => {
      oauthPopupRef.current?.close();
      toast.error('生成授权链接失败', { description: err instanceof ApiError ? err.message : '' });
    },
  });

  const completeOAuth = useMutation({
    mutationFn: () => adminAudit.completeCtidOAuth({ state: oauthState.trim(), code: oauthCode.trim() }),
    onSuccess: (data) => {
      if (data.syncWarning) {
        // token 已落库但账号同步失败:降级提示,可用「同步账号」补。
        toast.warning('cTID 已授权,但账号同步失败', { description: data.syncWarning });
      } else {
        toast.success('cTID 已授权', { description: `同步 ${data.accounts.length} 个账号` });
      }
      setOauthState('');
      setOauthCode('');
      setAuthUrl('');
      invalidate();
    },
    onError: (err) => toast.error('授权完成失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const refresh = useMutation({
    mutationFn: (id: string) => adminAudit.refreshCtidToken(id),
    onSuccess: (data) => {
      // 刷新是旋转式的:旧 token 此刻已在 cTrader 侧作废。后端会当场用新 token 探一次,
      // 探不通说明这个 cTID 已经断链(旧的没了、新的不能用),必须立刻重新授权 —— 这条
      // 绝不能用 success toast 一闪而过(2026-09-05 就是几小时后才发现)。
      if (data.tokenVerified === false) {
        toast.error('Token 已刷新,但新 Token 验证失败', {
          description: `${data.verifyError ?? '未知原因'} — 旧 Token 已作废,请立即重新走 OAuth 授权`,
          duration: 30_000,
        });
      } else {
        toast.success('Token 已刷新并验证通过', {
          description: `新过期时间 ${fmtDateTime(data.tokenExpiresAt)}${
            data.accountsVisible != null ? `,可见 ${data.accountsVisible} 个账号` : ''
          }`,
        });
      }
      invalidate();
    },
    onError: (err) => toast.error('刷新失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const sync = useMutation({
    mutationFn: (id: string) => adminAudit.syncCtidAccounts(id),
    onSuccess: (data) => {
      toast.success('账号已同步', { description: `同步 ${data.syncedAccounts} 个账号` });
      invalidate();
    },
    onError: (err) => toast.error('同步失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const selectMain = useMutation({
    mutationFn: (vars: { accountId: string; force: boolean; note?: string }) =>
      adminAudit.selectMainLiveAccount(vars.accountId, {
        force: vars.force,
        note: vars.note,
      }),
    onSuccess: (resp) => {
      toast.success(resp.forced ? '已强制写入主账号配置' : '主账号配置已写入', {
        description: `新账号 ${resp.newAccountId},后端重启后生效${
          resp.forced ? '(force:重启复检失败将标 ORPHANED 并停机)' : ''
        }`,
      });
      setSelectTarget(null);
      invalidate();
    },
    onError: (err) => toast.error('选择失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const del = useMutation({
    mutationFn: (id: string) => adminAudit.deleteCtid(id),
    onSuccess: () => {
      toast.success('已删除未授权 cTID');
      invalidate();
    },
    onError: (err) => toast.error('删除失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const data = listQ.data;
  const ctids = data?.items ?? [];

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Plug className="h-5 w-5 text-brand" />
            cTID 与连接
          </h1>
          <p className="text-sm text-ink-2 mt-1">授权 · 账号同步 · LIVE 主真实账号选择 · 连接事件</p>
        </div>
        <Button variant="outline" size="sm" onClick={invalidate}>
          <RefreshCw className="h-4 w-4" />
          重新拉取
        </Button>
      </header>

      <section className="grid grid-cols-1 xl:grid-cols-[1.1fr_1.9fr] gap-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-brand" />
              添加 cTID
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ctid-email">邮箱 / 备注</Label>
                <Input id="ctid-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="operator@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ctid-name">显示名</Label>
                <Input id="ctid-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="备用 LIVE 账号" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => {
                  // 必须在用户点击的同步调用栈里 window.open,异步 onSuccess 里再开大概率被拦截。
                  const popup = window.open('about:blank', '_blank');
                  if (popup) popup.opener = null;
                  oauthPopupRef.current = popup;
                  startOAuth.mutate();
                }}
                disabled={!email.trim() || startOAuth.isPending}
              >
                <ExternalLink className="h-4 w-4" />
                生成授权链接
              </Button>
              {authUrl && (
                <Button variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(authUrl)}>
                  <Copy className="h-4 w-4" />
                  复制链接
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.6fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label htmlFor="oauth-state">State</Label>
                <Input id="oauth-state" value={oauthState} onChange={(e) => setOauthState(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="oauth-code">Authorization code</Label>
                <Input id="oauth-code" value={oauthCode} onChange={(e) => setOauthCode(e.target.value)} />
              </div>
              <Button size="sm" onClick={() => completeOAuth.mutate()} disabled={!oauthState.trim() || !oauthCode.trim() || completeOAuth.isPending}>
                <CheckCircle2 className="h-4 w-4" />
                完成授权
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand" />
              当前主真实账号
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <StateBox label="运行时账号" value={data?.runtimeMainAccountId || '0'} tone="brand" />
            <StateBox label="已配置账号" value={data?.configuredMainAccountId || '0'} tone={data?.pendingRestart ? 'warn' : 'up'} />
            <StateBox label="生效状态" value={data?.pendingRestart ? '待重启' : '已生效'} tone={data?.pendingRestart ? 'warn' : 'up'} />
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {ctids.map((ctid) => {
          // 按 cTID 而非全局收敛 busy:操作 A 的 cTID 时不应把 B/C 卡片的按钮一起禁用。
          const ctidBusy =
            (refresh.isPending && refresh.variables === ctid.ctidId) ||
            (sync.isPending && sync.variables === ctid.ctidId) ||
            (del.isPending && del.variables === ctid.ctidId) ||
            (selectMain.isPending &&
              ctid.accounts.some((a) => a.ctidTraderAccountId === selectMain.variables?.accountId));
          return (
            <CtidCard
              key={ctid.ctidId}
              ctid={ctid}
              onRefresh={() => refresh.mutate(ctid.ctidId)}
              onSync={() => sync.mutate(ctid.ctidId)}
              onSelectMain={(accountId, login) => setSelectTarget({ accountId, login })}
              onDelete={() => {
                if (window.confirm(`确认删除未授权的 pending cTID「${ctid.displayName || ctid.email}」?`)) {
                  del.mutate(ctid.ctidId);
                }
              }}
              busy={ctidBusy}
            />
          );
        })}
        {listQ.isLoading && (
          <Card>
            <CardContent className="py-8 text-center text-ink-3 text-sm">加载中...</CardContent>
          </Card>
        )}
      </section>

      <SelectMainDialog
        target={selectTarget}
        pending={selectMain.isPending}
        onClose={() => !selectMain.isPending && setSelectTarget(null)}
        onConfirm={(force, note) =>
          selectTarget && selectMain.mutate({ accountId: selectTarget.accountId, force, note })
        }
      />

      <CtidEventLog />
    </div>
  );
}

function StateBox({ label, value, tone }: { label: string; value: string; tone: 'brand' | 'up' | 'warn' }) {
  return (
    <div className="rounded-md border border-line bg-bg-3 px-3 py-2">
      <div className="text-xs text-ink-3">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="num font-semibold text-ink-0 truncate">{value}</span>
        <Pill tone={tone}>{tone === 'warn' ? '待处理' : '正常'}</Pill>
      </div>
    </div>
  );
}

function CtidCard({
  ctid,
  onRefresh,
  onSync,
  onSelectMain,
  onDelete,
  busy,
}: {
  ctid: CtidStatus;
  onRefresh: () => void;
  onSync: () => void;
  onSelectMain: (accountId: string, login: string) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const days = tokenDaysLeft(ctid.tokenExpiresAt);
  const tTone = tokenTone(days);
  // 未授权(pending,无同步到账号)的误建行可删除;已授权(active)的不能删。
  const canDelete = ctid.status !== 'active' && ctid.accounts.length === 0;

  return (
    <Card className={tTone === 'down' ? 'border-down/60' : tTone === 'warn' ? 'border-warn/60' : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span className="min-w-0">
            <span className="block truncate">{ctid.displayName || ctid.email}</span>
            {ctid.displayName && <span className="block text-xs text-ink-3 truncate">{ctid.email}</span>}
          </span>
          <Pill tone={statusTone(ctid.status)}>{ctid.status ?? 'unknown'}</Pill>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <Meta label="cTID" value={ctid.ctidId} />
          <Meta label="App ID" value={ctid.openApiAppId ?? '-'} />
          <Meta label="Token" value={days != null ? (days >= 0 ? `还剩 ${days} 天` : '已过期') : '-'} tone={tTone} />
          <Meta label="同步" value={fmtDateTime(ctid.lastSyncedAt)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ctid.demo && <ConnRow kind="demo" state={ctid.demo.state} accountsAuthed={ctid.demo.accountsAuthed} since={ctid.demo.connectedSince} lastError={ctid.demo.lastError} />}
          {ctid.live && <ConnRow kind="live" state={ctid.live.state} accountsAuthed={ctid.live.accountsAuthed} since={ctid.live.connectedSince} lastError={ctid.live.lastError} />}
        </div>

        <div className="border-t border-line pt-2 space-y-2">
          {ctid.accounts.length === 0 ? (
            <div className="rounded-md bg-bg-3 px-3 py-3 text-xs text-ink-3">尚未同步到交易账号</div>
          ) : (
            ctid.accounts.map((account) => (
              <AccountRow key={account.ctidTraderAccountId} account={account} onSelectMain={onSelectMain} busy={busy} />
            ))
          )}
        </div>

        <div className="pt-2 border-t border-line flex items-center gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onSync} disabled={busy || ctid.status !== 'active'}>
            <RefreshCw className="h-3.5 w-3.5" />
            同步账号
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={onRefresh} disabled={busy || ctid.status !== 'active'}>
            <RotateCw className="h-3.5 w-3.5" />
            刷新 Token
          </Button>
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-down"
              onClick={onDelete}
              disabled={busy}
              title="删除未授权的 pending cTID"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Meta({ label, value, tone = 'neutral' }: { label: string; value?: string; tone?: 'up' | 'warn' | 'down' | 'neutral' }) {
  return (
    <div className="rounded-md bg-bg-3 px-2.5 py-2 min-w-0">
      <div className="text-[11px] text-ink-3">{label}</div>
      <div className="mt-0.5 num text-xs text-ink-1 truncate">
        {tone === 'neutral' ? value || '-' : <Pill tone={tone}>{value || '-'}</Pill>}
      </div>
    </div>
  );
}

function AccountRow({ account, onSelectMain, busy }: { account: CtidTradingAccount; onSelectMain: (accountId: string, login: string) => void; busy: boolean }) {
  const canSelect = account.environment === 'live' && !account.isConfiguredMain;
  return (
    <div className="rounded-md border border-line bg-bg-3 px-3 py-2 flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="num font-semibold text-ink-0">{account.accountLogin}</span>
          <Pill tone={account.environment === 'live' ? 'up' : 'neutral'}>{account.environment.toUpperCase()}</Pill>
          {account.isCurrentRuntimeMain && (
            <Pill tone="brand" className="gap-1">
              <Star className="h-3 w-3" />
              运行中
            </Pill>
          )}
          {account.isConfiguredMain && <Pill tone="warn">已配置</Pill>}
        </div>
        <div className="mt-1 text-xs text-ink-3 num truncate">
          {account.ctidTraderAccountId} · {account.currency ?? 'USD'} · {account.brokerTitleShort ?? 'broker -'}
        </div>
      </div>
      <Pill tone={account.connectionStatus === 'connected' ? 'up' : 'neutral'}>{account.connectionStatus}</Pill>
      <Button
        size="sm"
        variant="outline"
        disabled={!canSelect || busy}
        onClick={() => onSelectMain(account.ctidTraderAccountId, account.accountLogin)}
      >
        设为主账号
      </Button>
    </div>
  );
}

function SelectMainDialog({
  target,
  pending,
  onClose,
  onConfirm,
}: {
  target: { accountId: string; login: string } | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (force: boolean, note: string) => void;
}) {
  const [force, setForce] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (target) {
      setForce(false);
      setNote('');
    }
  }, [target]);

  const canSubmit = !!target && !pending;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warn">
            <ShieldCheck className="h-5 w-5" />
            设为主真实账号
          </DialogTitle>
          <DialogDescription>
            冷切换:只写入配置 + 审计,当前进程仍用旧账号,<strong>重启后端</strong>才生效。重启装配会再复检一次旧账号
            是否已腾空 —— 不安全则回退旧账号并紧急停机。请在交易窗口外、旧账号空仓时操作。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md bg-bg-3 px-3 py-2 text-sm">
            目标账号 <span className="num font-semibold text-ink-0">{target?.login}</span>
            <span className="text-ink-3 num"> ({target?.accountId})</span>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="select-main-note">备注(可选,记入审计)</Label>
            <Input
              id="select-main-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如:主账号 token 失效,切到备用 LIVE"
              disabled={pending}
            />
          </div>
          <label className="flex items-start gap-2 rounded-md border border-down/40 bg-down/10 px-3 py-2 text-xs text-down-dim cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              disabled={pending}
            />
            <span className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                强制切换(灾难通道):绕过未平仓 / 交易窗口阻断。仅在旧主账号已带单宕机 / token 永久失效时用 ——
                重启复检仍失败会把旧账号残留标 ORPHANED 并停机,需人工去 cTrader 平旧仓。
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            取消
          </Button>
          <Button
            variant={force ? 'destructive' : 'warn'}
            onClick={() => canSubmit && onConfirm(force, note.trim())}
            disabled={!canSubmit}
          >
            {pending ? '写入中...' : force ? '强制切换(待重启)' : '确认切换(待重启)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnRow({
  kind,
  state,
  accountsAuthed,
  since,
  lastError,
}: {
  kind: 'demo' | 'live';
  state?: string;
  accountsAuthed?: number;
  since?: string;
  lastError?: string;
}) {
  const tone = CONN_TONE(state);
  return (
    <div className="rounded-md bg-bg-3 px-2.5 py-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-ink-1">
          {tone === 'up' ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          <span className="font-semibold uppercase">{kind}</span>
          <Pill tone={tone}>{state ?? '-'}</Pill>
        </span>
        <span className="text-ink-3 num">{accountsAuthed != null ? `${accountsAuthed} 账号` : ''}</span>
      </div>
      {since && <div className="mt-1 text-[11px] text-ink-3 num">连接于 {fmtDateTime(since)}</div>}
      {lastError && <div className="mt-1 text-[11px] text-down-dim num">last error: {lastError}</div>}
    </div>
  );
}
